/**
 * Setups, mistakes and everything else — picked from a vocabulary, not typed.
 *
 * ## Why this is not a free-text field
 *
 * `strategy.ts` states the failure: "a journal with forty tags, half of them
 * synonyms, and no way to answer 'is this setup working'". A text input
 * produces that within a month — `gap and go`, `gap-and-go` and `Gap & Go` are
 * one setup in the trader's head and three rows in every report.
 *
 * So the picker offers what exists and creating a new tag is a deliberate,
 * visible act. The server folds case and whitespace, so a user who types a
 * label that already exists gets the existing tag rather than a duplicate.
 *
 * ## Why three kinds
 *
 * A setup and a mistake are not the same species of fact. "opening drive" is
 * what you were trying to do; "added to loser" is what went wrong while doing
 * it. Kept apart, the journal can answer which mistakes cost the most inside
 * the setups that otherwise work. Merged into one cloud, it cannot.
 *
 * ## Why the suggestions are only suggestions
 *
 * The vocabulary ships empty. A pre-filled list of setups would be a house
 * trading style, and CLAUDE.md's non-negotiable is that AEGIS does not have
 * one. The suggestion row is offered, dismissible, and creates nothing until
 * the trader clicks a specific one.
 */
import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { api, ApiError } from '../lib/api.js';
import type { TagKind, TagWithUse } from '@aegis/api-client';
import { Icon } from './Icon.js';

const KINDS: readonly { id: TagKind; label: string; hint: string }[] = [
  { id: 'setup', label: 'Setups', hint: 'What you were trying to do' },
  { id: 'mistake', label: 'Mistakes', hint: 'What went wrong while you did it' },
  { id: 'custom', label: 'Custom', hint: 'Anything that is neither' },
];

export interface TagPickerProps {
  /** Currently applied tag ids. */
  value: readonly string[];
  onChange(next: string[]): void;
  disabled?: boolean;
}

export function TagPicker({ value, onChange, disabled = false }: TagPickerProps): JSX.Element {
  const [vocabulary, setVocabulary] = useState<TagWithUse[]>([]);
  const [suggestions, setSuggestions] = useState<{ kind: TagKind; label: string }[]>([]);
  const [openKind, setOpenKind] = useState<TagKind | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    const [tags, sugg] = await Promise.all([api.tags(), api.tagSuggestions()]);
    setVocabulary(tags.tags);
    setSuggestions(sugg.suggestions);
  };

  useEffect(() => {
    let live = true;
    void api.tags()
      .then(async (t) => {
        if (!live) return;
        setVocabulary(t.tags);
        const sugg = await api.tagSuggestions();
        if (live) setSuggestions(sugg.suggestions);
      })
      .catch(() => { if (live) setError('Could not load your tags.'); });
    return () => { live = false; };
  }, []);

  const applied = useMemo(() => new Set(value), [value]);
  const byId = useMemo(() => new Map(vocabulary.map((t) => [t.id, t])), [vocabulary]);

  /**
   * The picker offers live tags only. An archived tag already on this trade
   * still renders as a chip — archiving takes a tag out of circulation, it does
   * not rewrite the trades that already carry it.
   */
  const offered = (kind: TagKind): TagWithUse[] =>
    vocabulary.filter((t) => t.kind === kind && t.archivedAt === null);

  const toggle = (id: string): void => {
    onChange(applied.has(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  const create = async (label: string, kind: TagKind): Promise<void> => {
    const trimmed = label.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Idempotent server-side: typing a label that already exists returns the
      // existing tag, so this cannot produce the duplicate it looks like it
      // might.
      const tag = await api.createTag(trimmed, kind);
      await reload();
      if (!applied.has(tag.id)) onChange([...value, tag.id]);
      setDraft('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create that tag.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tagpicker">
      {KINDS.map(({ id, label, hint }) => {
        const chosen = vocabulary.filter((t) => t.kind === id && applied.has(t.id));
        const open = openKind === id;
        return (
          <div key={id} className="tagkind">
            <div className="tagkind-head">
              <span className="field-label">{label}</span>
              <button
                type="button"
                className="btn-ghost tagkind-add"
                onClick={() => { setOpenKind(open ? null : id); setDraft(''); }}
                disabled={disabled}
                aria-expanded={open}
              >
                <Icon name={open ? 'close' : 'plus'} size="sm" />
                <span className="sr-only">{open ? `Close ${label}` : `Add to ${label}`}</span>
              </button>
            </div>

            <div className="tagchips">
              {chosen.length === 0 && !open && <span className="dim tagkind-hint">{hint}</span>}
              {chosen.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`tagchip on tagchip-${t.kind}`}
                  onClick={() => toggle(t.id)}
                  disabled={disabled}
                >
                  {t.label}
                  {t.archivedAt && <span className="tagchip-archived" title="Archived"> ·</span>}
                  <Icon name="close" size="sm" />
                </button>
              ))}
            </div>

            {open && (
              <div className="tagmenu">
                <div className="tagmenu-list">
                  {offered(id).filter((t) => !applied.has(t.id)).map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`tagchip tagchip-${t.kind}`}
                      onClick={() => toggle(t.id)}
                    >
                      {t.label}
                      <span className="tagchip-count mono">{t.tradeCount}</span>
                    </button>
                  ))}
                  {offered(id).length === 0 && (
                    <span className="dim tagkind-hint">
                      Nothing here yet. Type one below, or take a suggestion.
                    </span>
                  )}
                </div>

                <div className="tagmenu-new">
                  <input
                    className="input"
                    value={draft}
                    maxLength={40}
                    placeholder={`New ${id}…`}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); void create(draft, id); }
                      if (e.key === 'Escape') setOpenKind(null);
                    }}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => void create(draft, id)}
                    disabled={busy || draft.trim() === ''}
                  >
                    Create
                  </button>
                </div>

                {suggestions.some((s) => s.kind === id) && (
                  <div className="tagsuggest">
                    <span className="dim tagkind-hint">Suggestions — nothing is created until you pick one:</span>
                    <div className="tagmenu-list">
                      {suggestions.filter((s) => s.kind === id).map((s) => (
                        <button
                          key={s.label}
                          type="button"
                          className="tagchip tagchip-suggested"
                          onClick={() => void create(s.label, id)}
                          disabled={busy}
                        >
                          + {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* An applied tag whose vocabulary row has not loaded yet, or was deleted
          in another tab. Shown as its raw id rather than dropped, because
          silently discarding a tag the user applied is worse than an ugly
          chip. */}
      {value.filter((id) => !byId.has(id)).map((id) => (
        <span key={id} className="tagchip tagchip-unknown mono" title="This tag is no longer in your vocabulary">
          {id}
        </span>
      ))}

      {error && <p className="form-error">{error}</p>}
    </div>
  );
}
